import React from 'react'
import { useTranslation } from 'react-i18next'
import { Col, Row } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { dateParse } from 'helpers/date.helper'
import StateMonitor from 'components/ui/state-monitor/StateMonitor'
import { MonitoringStatus } from 'enum'
import FileBlock from 'components/ui/forms/field/awesomeFileInput/components/FileBlock'
import { UploadFileStatusEnum } from 'enum/uploadFile-status.enum'
import { IMainState } from 'store/states'
import { useTranslationRankmi } from 'helpers/translations.helper'
import FinalState from 'components/synchronousCourses/FinalState'
import EvaluationResults from 'components/monitoring/components/Course/components/Statistics/components/Users/components/evaluations-results/EvaluationResults'
import { useEvaluationsMax } from '../common'
import styles from './CourseResults.module.scss'

/**
 * If the course is imported, the date is adjusted to the local timezone
 */
const adjustDate = (date?: string, imported?: boolean) =>
  imported ? date?.replace('Z', '') : date

export const CourseResults: React.FC = () => {
  const { t } = useTranslation()
  const tRelative = useTranslationRankmi({
    prefix: 'administration.monitor.participantResults',
  })
  const { user } = useSelector(({ monitoring }: IMainState) => monitoring)
  const { synchronousCourseEnrol, files, course } = user
  const { advance, mandatory, lessons, evaluations, imported } = {
    ...course,
  }

  const startDate = adjustDate(course?.startDate, imported)
  const finishDate = adjustDate(course?.finishDate, imported)

  const {
    synchronousCourseEnrolAssessments: assessments,
    finalState,
    attendance,
  } = {
    ...synchronousCourseEnrol,
  }
  const finishedLessons = (lessons: any) => {
    if (lessons) {
      const { total, finish } = lessons
      return `${t('administration.monitor.finish', {
        init: finish || 0,
        total: total || 0,
      })} (${total > 0 ? ((finish / total) * 100).toFixed(0) + '%' : '0%'})`
    }
    return ''
  }

  const handleDownload = (url: string, name: string) => {
    const element = document.createElement('a')
    element.setAttribute('href', url)
    element.setAttribute('target', '_blank')
    element.setAttribute('download', name)
    element.style.display = 'none'
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  }
  const evaluationsMax = useEvaluationsMax()

  const isSynchronous = !!synchronousCourseEnrol
  return (
    <>
      <Row>
        <Col xs={12}>
          <h1 className="mb-4">{tRelative('title')}</h1>
          <Row className={`mb-4 ${styles.courseResult}`}>
            <Col xs={3}>
              <h2 className={styles.title}>{tRelative('CourseType.title')}</h2>
              <p className={styles.normalText}>
                {tRelative(
                  `CourseType.${mandatory === 0 ? 'optional' : 'mandatory'}`
                )}
              </p>
            </Col>
            <Col xs={3}>
              <h2 className={styles.title}>{tRelative('finishedLessons')}</h2>
              <p className={`${styles.normalText} mb-1`}>
                {finishedLessons(lessons)}
              </p>
            </Col>
            <Col xs={3}>
              <h2 className={styles.title}>{tRelative('courseState')}</h2>
              <StateMonitor state={advance || 0} />
            </Col>
            {isSynchronous && (
              <Col xs={3}>
                <h2 className={styles.title}>{tRelative('finalState')}</h2>
                <span className={`${styles.normalText} mb-1`}>
                  {(!!finalState && <FinalState state={finalState} />) || '-'}
                </span>
              </Col>
            )}
            {isSynchronous && (
              <Col xs={3}>
                <h2 className={styles.title}>{tRelative('attendance')}</h2>
                <div className={`${styles.normalText} mb-1 text-center`}>
                  {attendance ? <>{attendance}%</> : '-'}
                </div>
              </Col>
            )}
            {isSynchronous && (
              <Col xs={12} className="p-0">
                <h2 className={styles.title}>{tRelative('evaluations')}</h2>
                <Row className="pl-2">
                  {[...new Array(evaluationsMax)].map((_, index) => (
                    <Col key={index}>
                      <h2 className={styles.subtitle}>
                        {tRelative('evaluation', { count: index + 1 })}
                      </h2>
                      <span className={styles.textSmall}>
                        {assessments?.[index]?.qualification || '--'}
                      </span>
                    </Col>
                  ))}
                </Row>
              </Col>
            )}
            {evaluations && evaluations.total > 0 && (
              <Col>
                <h2 className={styles.title}>
                  {tRelative('courseEvaluation')}
                </h2>
                <EvaluationResults
                  results={{
                    approved: evaluations.approved,
                    failed: evaluations.failed,
                    total: evaluations.total,
                    order: evaluations.order,
                  }}
                  inline={true}
                />
              </Col>
            )}
          </Row>
          <Row className="mb-5">
            <Col sm={12} md={4}>
              <h2 className={styles.title}>{tRelative('history')}</h2>
              {startDate ? (
                <>
                  <p className={`${styles.normalText} mb-1`}>
                    {' '}
                    {`${tRelative('startedOn')} ${dateParse(startDate)}`}{' '}
                  </p>
                  {finishDate && advance === MonitoringStatus.Finish && (
                    <p className={styles.normalText}>{`${tRelative(
                      'finishedOn'
                    )} ${dateParse(finishDate)}`}</p>
                  )}
                </>
              ) : (
                <p className={`${styles.normalText} mb-1`}>
                  {tRelative('unstarted')}
                </p>
              )}
            </Col>
          </Row>
          {files && files.length > 0 && (
            <>
              <h2 className={styles.title}>
                {tRelative('files.uploadedFiles')}
              </h2>
              <Row>
                <Col xs={12} md={6} className="mb-5">
                  {files.map(
                    ({ id, fileUrl, updatedAt, filename, size }: any) => {
                      return (
                        <div
                          key={id + fileUrl}
                          onClick={() => handleDownload(fileUrl, filename)}
                        >
                          <FileBlock
                            className={styles.blockContainer}
                            data={{
                              id,
                              fileUrl,
                              name: filename,
                              size,
                              progress: 0,
                              status: UploadFileStatusEnum.completed,
                            }}
                            disabled={true}
                          />
                          <div className={`${styles.uploadDate} mb-3`}>
                            {' '}
                            {`${tRelative('files.sendOn')} ${dateParse(
                              updatedAt
                            )}`}
                          </div>
                        </div>
                      )
                    }
                  )}
                </Col>
              </Row>
            </>
          )}
        </Col>
      </Row>
    </>
  )
}

export default CourseResults
